/**
 * .recipe-steps 内の h3 ごとに後続要素を <div class="recipe-step"> でラップする
 *
 * 同一ステップ内に画像付き <p> が 2 件以上あるときだけ <div class="recipe-step__images"> で包み縦並びにする。
 * 1 件のみのときは従来どおり step 直下の <p> のまま（2 列グリッドの右列に 1 枚）。
 *
 * 変換前:
 *   <div class="recipe-steps">
 *     <h3>手順1</h3>
 *     <p>説明...</p>
 *     <h3>手順2</h3>
 *     <p>説明...</p>
 *   </div>
 *
 * 変換後:
 *   <div class="recipe-steps">
 *     <div class="recipe-step">
 *       <h3>手順1</h3>
 *       <p>説明...</p>
 *     </div>
 *     <div class="recipe-step">
 *       <h3>手順2</h3>
 *       <p>説明...</p>
 *     </div>
 *   </div>
 */
export function initRecipeContent(): void {
  initRecipeSteps();
  initMainBlogRecipeLoadMore();
}

function initRecipeSteps(): void {
  const container = document.querySelector<HTMLElement>(".recipe-steps");
  if (!container) return;

  // ES5 互換: childNodes を静的配列にコピー
  const children: Node[] = [].slice.call(container.childNodes);

  // h3 ごとにノードを分割してグループ化
  const groups: Node[][] = [];
  let current: Node[] | null = null;

  for (let i = 0; i < children.length; i++) {
    const node = children[i];
    const isH3 =
      node.nodeType === Node.ELEMENT_NODE &&
      (node as Element).tagName === "H3";

    if (isH3) {
      current = [];
      groups.push(current);
    }

    // 最初の h3 より前のノードは無視
    if (current === null) continue;

    current.push(node);
  }

  // h3 が存在しなければ何もしない
  if (groups.length === 0) return;

  // コンテナを空にする（ES5 互換）
  while (container.firstChild) {
    container.removeChild(container.firstChild);
  }

  // 各グループを .recipe-step でラップして追加
  for (let g = 0; g < groups.length; g++) {
    const step = document.createElement("div");
    step.className = "recipe-step";
    const nodes = groups[g];

    // h3・テキスト p は .recipe-step__caption へ。画像 p は 同一ステップで 2 枚以上なら縦並び用ラッパーへ
    const caption = document.createElement("div");
    caption.className = "recipe-step__caption";
    const imageParagraphs: HTMLElement[] = [];

    for (let n = 0; n < nodes.length; n++) {
      const node = nodes[n];
      const isImgP =
        node.nodeType === Node.ELEMENT_NODE &&
        (node as Element).tagName === "P" &&
        (node as Element).querySelector("img") !== null;

      if (isImgP) {
        imageParagraphs.push(node as HTMLElement);
      } else {
        caption.appendChild(node);
      }
    }

    step.insertBefore(caption, step.firstChild);

    if (imageParagraphs.length >= 2) {
      const imagesWrap = document.createElement("div");
      imagesWrap.className = "recipe-step__images";
      for (let i = 0; i < imageParagraphs.length; i += 1) {
        imagesWrap.appendChild(imageParagraphs[i]);
      }
      step.appendChild(imagesWrap);
    } else if (imageParagraphs.length === 1) {
      step.appendChild(imageParagraphs[0]);
    }

    const hasImage = imageParagraphs.length > 0;
    if (!hasImage) {
      step.className = "recipe-step is-no-image";
    }

    container.appendChild(step);
  }
}

const MAIN_BLOG_RECIPE_SELECTOR = "[data-main-blog-recipe]";
const MAIN_BLOG_RECIPE_LIST_SELECTOR = "[data-main-blog-recipe-list]";
const MAIN_BLOG_RECIPE_CARD_SELECTOR = "[data-main-blog-recipe-card]";
const MAIN_BLOG_RECIPE_LOAD_MORE_SELECTOR = "[data-main-blog-recipe-load-more]";
const HIDDEN_CARD_SELECTOR = `${MAIN_BLOG_RECIPE_CARD_SELECTOR}.tw-hidden`;

function countHiddenCardsInList(list: HTMLElement): number {
  return list.querySelectorAll(HIDDEN_CARD_SELECTOR).length;
}

function revealNextBatchInList(list: HTMLElement, batchSize: number): void {
  const hiddenCards = list.querySelectorAll<HTMLElement>(HIDDEN_CARD_SELECTOR);
  const revealCount = Math.min(batchSize, hiddenCards.length);

  for (let i = 0; i < revealCount; i += 1) {
    hiddenCards.item(i).classList.remove("tw-hidden");
  }
}

function initMainBlogRecipeLoadMore(): void {
  const rootNodeList = document.querySelectorAll<HTMLElement>(MAIN_BLOG_RECIPE_SELECTOR);
  if (!rootNodeList.length) return;
  const roots: HTMLElement[] = [].slice.call(rootNodeList);

  for (let r = 0; r < roots.length; r += 1) {
    initMainBlogRecipeLoadMoreInRoot(roots[r]);
  }
}

function initMainBlogRecipeLoadMoreInRoot(root: HTMLElement): void {
  let batchSize = parseInt(root.dataset.recipeBatchSize || "", 10);
  if (!batchSize || batchSize < 1) {
    batchSize = 1;
  }

  const loadMoreNodeList = root.querySelectorAll<HTMLElement>(MAIN_BLOG_RECIPE_LOAD_MORE_SELECTOR);
  if (!loadMoreNodeList.length) return;
  const loadMoreWraps: HTMLElement[] = [].slice.call(loadMoreNodeList);

  for (let i = 0; i < loadMoreWraps.length; i += 1) {
    const loadMoreWrap = loadMoreWraps[i];
    const brand = (loadMoreWrap.dataset.brand || "").trim();
    if (!brand) continue;

    const list = root.querySelector<HTMLElement>(
      `${MAIN_BLOG_RECIPE_LIST_SELECTOR}[data-main-blog-recipe-list="${brand}"]`,
    );
    if (!list) {
      loadMoreWrap.classList.add("tw-hidden");
      continue;
    }

    const loadMoreLink = loadMoreWrap.querySelector<HTMLAnchorElement>("a[href]");
    if (!loadMoreLink) continue;

    const updateButtonState = () => {
      if (countHiddenCardsInList(list) > 0) {
        loadMoreWrap.classList.remove("tw-hidden");
      } else {
        loadMoreWrap.classList.add("tw-hidden");
      }
    };

    loadMoreLink.addEventListener("click", (event) => {
      if (countHiddenCardsInList(list) === 0) return;

      event.preventDefault();
      revealNextBatchInList(list, batchSize);
      updateButtonState();
    });

    updateButtonState();
  }
}
